import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { pageById } from '../lib/registry';
import { useAdminSession } from '../lib/session';
import { StateCard } from '../components/StateCard';

function AuthLayout({ pageId, children }: { pageId: string; children: React.ReactNode }) {
  const page = pageById.get(pageId);

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, background: 'var(--surface-muted)' }}>
      <div className="card" style={{ width: 'min(420px, 100%)' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <img src="/ak-mark.png" alt="" width={32} height={32} style={{ borderRadius: 8 }} />
          <div>
            <div style={{ fontWeight: 700 }}>{page?.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>ASKRS Games 운영 콘솔</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A001 — operator sign-in. Session cookie only; no token is stored in the browser. */
export function AdminSignIn() {
  const navigate = useNavigate();
  const session = useAdminSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api.admin<{ next_step: string }>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      await session.refresh();
      // 'ready' = this deployment has second factors turned off.
      navigate(response.data.next_step === 'ready' ? '/admin' : '/admin/challenge');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout pageId="A001">
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="admin-email">이메일</label>
          <input id="admin-email" type="email" autoComplete="username" required
            value={email} onChange={(event) => setEmail(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="admin-password">비밀번호</label>
          <input id="admin-password" type="password" autoComplete="current-password" required
            value={password} onChange={(event) => setPassword(event.target.value)} />
        </div>
        {error && <div style={{ marginBottom: 12 }}><StateCard state={error.screenState as never} error={error} /></div>}
        <button type="submit" className="btn btn--primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? '확인 중…' : '로그인'}
        </button>
      </form>
      <p style={{ marginTop: 16, fontSize: 13 }}>
        <Link to="/admin/recovery">비밀번호 찾기</Link>
      </p>
    </AuthLayout>
  );
}

/** A002 — MFA challenge. Sensitive permissions stay locked until this passes. */
export function AdminChallenge() {
  const navigate = useNavigate();
  const session = useAdminSession();
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<'totp' | 'recovery'>('totp');
  const [error, setError] = useState<ApiError | null>(null);
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauth_url: string; recovery_codes: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const challenge = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const endpoint = enrollment ? '/auth/mfa/confirm' : '/auth/challenge';
      await api.admin(endpoint, { method: 'POST', body: enrollment ? { code } : { code, kind } });
      await session.refresh();
      navigate('/admin');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
    } finally {
      setBusy(false);
    }
  };

  const enroll = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await api.admin<{ secret: string; otpauth_url: string; recovery_codes: string[] }>(
        '/auth/mfa/enroll', { method: 'POST' },
      );
      setEnrollment(response.data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout pageId="A002">
      {enrollment ? (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13 }}>인증 앱에 아래 키 등록 후 코드 입력</p>
          <code style={{ display: 'block', padding: 12, background: 'var(--surface-muted)', borderRadius: 6, wordBreak: 'break-all' }}>
            {enrollment.secret}
          </code>
          <p style={{ fontSize: 13, marginTop: 12 }}>복구 코드 (한 번만 표시)</p>
          <ul style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            {enrollment.recovery_codes.map((recoveryCode) => <li key={recoveryCode}>{recoveryCode}</li>)}
          </ul>
        </div>
      ) : (
        <div className="field">
          <label htmlFor="challenge-kind">인증 방식</label>
          <select id="challenge-kind" value={kind} onChange={(event) => setKind(event.target.value as 'totp' | 'recovery')}>
            <option value="totp">인증 앱 코드</option>
            <option value="recovery">복구 코드</option>
          </select>
        </div>
      )}

      <form onSubmit={challenge}>
        <div className="field">
          <label htmlFor="challenge-code">인증 코드</label>
          <input id="challenge-code" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(event) => setCode(event.target.value)} />
        </div>
        {error && <div style={{ marginBottom: 12 }}><StateCard state={error.screenState as never} error={error} /></div>}
        <button type="submit" className="btn btn--primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? '확인 중…' : '인증'}
        </button>
      </form>

      {!enrollment && (
        <button type="button" className="btn" style={{ width: '100%', marginTop: 12 }} onClick={() => void enroll()} disabled={busy}>
          다중 인증 등록하기
        </button>
      )}
    </AuthLayout>
  );
}

/** A003 — operator recovery. Never discloses whether an account exists. */
export function AdminRecovery() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (token) {
        await api.admin('/auth/recovery/confirm', { method: 'POST', body: { token, password } });
      } else {
        await api.admin('/auth/recovery', { method: 'POST', body: { email } });
      }
      setDone(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
    }
  };

  return (
    <AuthLayout pageId="A003">
      {done ? (
        <p style={{ fontSize: 14 }}>
          {token
            ? '비밀번호 재설정 완료 · 기존 로그인 모두 해제'
            : '등록된 계정이면 복구 메일 발송'}
        </p>
      ) : (
        <form onSubmit={submit}>
          {token ? (
            <div className="field">
              <label htmlFor="new-password">새 비밀번호 (12자 이상)</label>
              <input id="new-password" type="password" minLength={12} required
                value={password} onChange={(event) => setPassword(event.target.value)} />
            </div>
          ) : (
            <div className="field">
              <label htmlFor="recovery-email">이메일</label>
              <input id="recovery-email" type="email" required
                value={email} onChange={(event) => setEmail(event.target.value)} />
            </div>
          )}
          {error && <div style={{ marginBottom: 12 }}><StateCard state={error.screenState as never} error={error} /></div>}
          <button type="submit" className="btn btn--primary" style={{ width: '100%' }}>
            {token ? '재설정' : '복구 요청'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

/** A004 — invitation acceptance. */
export function AdminInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await api.admin('/auth/invitations/accept', { method: 'POST', body: { token, name, password } });
      setDone(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
    }
  };

  if (!token) {
    return <AuthLayout pageId="A004"><StateCard state="EXPIRED" /></AuthLayout>;
  }

  return (
    <AuthLayout pageId="A004">
      {done ? (
        <>
          <p style={{ fontSize: 14 }}>계정 설정 완료 · 2단계 인증 사용 시 인증 앱 등록 필요</p>
          <Link className="btn btn--primary" to="/admin/sign-in" style={{ marginTop: 12 }}>로그인으로 이동</Link>
        </>
      ) : (
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="invite-name">이름</label>
            <input id="invite-name" required value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="invite-password">비밀번호 (12자 이상)</label>
            <input id="invite-password" type="password" minLength={12} required
              value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
          {error && <div style={{ marginBottom: 12 }}><StateCard state={error.screenState as never} error={error} /></div>}
          <button type="submit" className="btn btn--primary" style={{ width: '100%' }}>계정 설정</button>
        </form>
      )}
    </AuthLayout>
  );
}
